import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Zap, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEXTS = {
  es: { upgrade: '¡Sube de nivel!', noThanks: 'No, gracias', accept: 'Quiero esto', instead: 'en vez de', for: 'por solo' },
  en: { upgrade: 'Level up!', noThanks: 'No, thanks', accept: 'I want this', instead: 'instead of', for: 'for only' },
  cat: { upgrade: 'Puja de nivell!', noThanks: 'No, gràcies', accept: 'Vull això', instead: 'en lloc de', for: 'per només' },
  fr: { upgrade: 'Montez en gamme !', noThanks: 'Non, merci', accept: 'Je veux ça', instead: 'au lieu de', for: 'pour seulement' },
};

const UpsellPopup = ({ banner, upsellProduct, triggerProduct, onAccept, onDecline }) => {
  const { language, localize } = useLanguage();
  const t = TEXTS[language] || TEXTS.es;

  const upsellName = localize(upsellProduct.name || upsellProduct.nombre);
  const upsellPrice = upsellProduct.price;
  const triggerName = localize(triggerProduct.name || triggerProduct.nombre);
  const triggerPrice = triggerProduct.price;
  const imageUrl = banner.image_url
    ? (banner.image_url.startsWith('/api') ? `${API_URL}${banner.image_url}` : banner.image_url)
    : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      data-testid="upsell-popup"
    >
      <div className="w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom-4 duration-300" style={{ backgroundColor: 'var(--sand-50)' }}>
        {/* Image banner */}
        {imageUrl && (
          <div className="relative h-44 overflow-hidden">
            <img src={imageUrl} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <button
              onClick={onDecline}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
              data-testid="upsell-close-btn"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-5">
          {!imageUrl && (
            <button onClick={onDecline} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 text-gray-400">
              <X size={18} />
            </button>
          )}

          <div className="flex items-center gap-2 mb-2">
            <Zap size={18} style={{ color: 'var(--gold)' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--gold-dark)' }}>{t.upgrade}</span>
          </div>

          <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--stone-800)' }}>{banner.title}</h3>
          <p className="text-sm mb-4" style={{ color: 'var(--stone-500)' }}>{banner.description}</p>

          {/* Price comparison */}
          <div className="flex items-center gap-3 p-3 rounded-xl border mb-4" style={{ background: 'linear-gradient(135deg, rgba(183,140,60,0.08), rgba(212,175,55,0.08))', borderColor: 'rgba(183,140,60,0.2)' }}>
            <div className="text-center">
              <p className="text-xs line-through" style={{ color: 'var(--stone-400)' }}>{triggerName}</p>
              <p className="text-sm line-through" style={{ color: 'var(--stone-400)' }}>{triggerPrice.toFixed(2)}€</p>
            </div>
            <ChevronRight size={18} style={{ color: 'var(--gold)' }} className="shrink-0" />
            <div className="text-center flex-1">
              <p className="text-sm font-semibold" style={{ color: 'var(--stone-800)' }}>{upsellName}</p>
              <p className="text-lg font-bold" style={{ color: 'var(--gold)' }}>{upsellPrice.toFixed(2)}€</p>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-2">
            <Button
              onClick={onAccept}
              className="w-full py-5 rounded-full text-base font-semibold text-white border-0"
              style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}
              data-testid="upsell-accept-btn"
            >
              {t.accept} — {upsellPrice.toFixed(2)}€
            </Button>
            <button
              onClick={onDecline}
              className="w-full py-2.5 text-sm transition-colors"
              style={{ color: 'var(--stone-400)' }}
              data-testid="upsell-decline-btn"
            >
              {t.noThanks}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpsellPopup;
